import React, { useCallback } from 'react';
import { useCarouselContext } from '../Carousel.context';
import { useCarouselArrowButtons } from './useCarouselArrowButtons';
import { CarouselButtonProps, CarouselButton } from '../CarouselButton/CarouselButton';
import { ButtonBaseProps } from '../../ButtonBase';
import { ChevronLeftIcon, ChevronRightIcon } from '../../../icons';

export interface CarouselArrowButtonsProps
    extends Pick<CarouselButtonProps, 'hiddenForTouch' | 'color' | 'withBorder' | 'size'> {
    /**
     * Additonal properties applied specifically to the `previous` button.
     */
    PrevButtonProps?: Partial<ButtonBaseProps>;

    /**
     * Additonal properties applied specifically to the `next` button.
     */
    NextButtonProps?: Partial<ButtonBaseProps>;
}

export function CarouselArrowButtons(props: Readonly<CarouselArrowButtonsProps>): React.JSX.Element {
    const { PrevButtonProps, NextButtonProps, ...sharedProps } = props;
    const { emblaApi, markUserInitiated } = useCarouselContext();

    const { prev: prevState, next: nextState } = useCarouselArrowButtons(emblaApi);

    const { onClick: onClickPrev, ...otherPrevProps } = PrevButtonProps || {};
    const { onClick: onClickNext, ...otherNextProps } = NextButtonProps || {};

    const handleClickPrev = useCallback(
        (event: React.SyntheticEvent) => {
            markUserInitiated();
            prevState.onClick();
            onClickPrev?.(event);
        },
        [onClickPrev, prevState, markUserInitiated]
    );

    const handleClickNext = useCallback(
        (event: React.SyntheticEvent) => {
            markUserInitiated();
            nextState.onClick();
            onClickNext?.(event);
        },
        [onClickNext, nextState, markUserInitiated]
    );

    return (
        <>
            {!prevState.hidden && (
                <CarouselButton
                    direction="prev"
                    onClick={handleClickPrev}
                    icon={<ChevronLeftIcon biDirectional={true} />}
                    hiddenForTouch={true}
                    disabled={prevState.disabled}
                    aria-label="Previous slide"
                    data-carousel-arrow-button="prev"
                    {...sharedProps}
                    {...otherPrevProps}
                />
            )}
            {!nextState.hidden && (
                <CarouselButton
                    direction="next"
                    onClick={handleClickNext}
                    icon={<ChevronRightIcon biDirectional={true} />}
                    hiddenForTouch={true}
                    disabled={nextState.disabled}
                    aria-label="Next slide"
                    data-carousel-arrow-button="next"
                    {...sharedProps}
                    {...otherNextProps}
                />
            )}
        </>
    );
}
